import { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import Helmet from "react-helmet";
import { useHistory, useParams } from "react-router-dom";
import Split from "react-split-grid";
import styled from "styled-components";
import { Dialog } from "@blueprintjs/core";
import { useHotkeys } from "@mantine/hooks";
import { useAsyncEffect } from "@react-hook/async";

import { useOrg } from "@hooks";
import { PermissionsContext, UserContext } from "@providers";

import { RuleShareDialog } from "@pages/editor/components/RuleShareDialog";
import { WorkbenchContext } from "@pages/editor/providers";
import { Workbench } from "@pages/editor/stores";
import { UrlParams } from "@pages/editor/types";

import ImportRulesDialog from "./components/FileBrowser/ImportRulesPanel";
import {
  ConfirmDeleteAlert,
  EditorsPanel,
  FileBrowser,
  SplashPanel,
  SplitGutter,
} from "./components";

const Grid = styled.div<{ isPlayground: boolean }>`
  height: 100%;
  overflow-y: hidden;
  display: grid;
  grid-template-columns: 1fr 4px 4fr;
`;

const Screen = () => {
  const { bundleAddress } = useParams<UrlParams>();
  const history = useHistory();
  const { user } = useContext(UserContext);
  const org = useOrg();
  const { permissions } = useContext(PermissionsContext);

  const [workbench] = useState(
    () =>
      new Workbench(
        history,
        permissions,
        bundleAddress,
        history.location.pathname.startsWith("/playground")
      )
  );

  useAsyncEffect(workbench.reloadAllData, []);
  useEffect(() => {
    // Makes sure address string is always set to current url params
    if (bundleAddress !== workbench.addressString) {
      workbench.setAddressString(bundleAddress);
    }
  }, [workbench, bundleAddress]);

  useEffect(() => {
    workbench.setContext(user, org);
  }, [workbench, user, org]);

  useHotkeys([
    [
      "mod+S",
      (e) => {
        e.preventDefault();
        if (workbench.hasUnsavedChanges) {
          workbench.saveOrFork();
        }
      },
    ],
    [
      "mod+Enter",
      () => {
        workbench.bundle?.run();
      },
    ],
  ]);

  const pageName = workbench.isPlayground ? "Playground" : "Editor";

  return (
    <>
      <Helmet>
        <title>{`${pageName} | Semgrep`}</title>
      </Helmet>
      <WorkbenchContext.Provider value={{ workbench }}>
        {workbench.ui.isRuleShareDialogOpen && <RuleShareDialog />}
        <Dialog
          isOpen={workbench.ui.isImportRulesDialogOpen}
          onClose={() => {
            workbench.ui.closeImportRulesDialog();
          }}
          title="Import Rules"
        >
          <ImportRulesDialog name={workbench.bundle?.rule?.id || ""} />
        </Dialog>
        <Split
          snapOffset={180}
          render={({ getGridProps, getGutterProps }: any) => (
            <Grid {...getGridProps()}>
              <FileBrowser />
              <SplitGutter
                between="left-right"
                {...getGutterProps("column", 1)}
              />
              <ConfirmDeleteAlert />
              {bundleAddress ? <EditorsPanel /> : <SplashPanel />}
            </Grid>
          )}
        />
      </WorkbenchContext.Provider>
    </>
  );
};

export const EditorScreen = observer(Screen);
